/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Own high-frequency roster selection and guardian channel choice.
 * @state-owner module-scoped Zustand
 * @allowed-imports Zustand; shared view types; shared transient reset registry
 * @forbidden server entities; roles; permissions; tokens
 * @public-api classrooms module only
 * @side-effects in-memory UI updates
 * @tests modules/classrooms/model/next-lesson.test.ts and module-contracts.test.ts
 */

import { create } from 'zustand';
import type { GuardianChannel } from '../../../shared/lib/model';
import { registerWorkspaceTransientReset } from '../../../shared/lib/transient-reset';

interface ClassroomUiState {
  selectedStudents: readonly string[];
  guardianChannel: GuardianChannel;
  presentingFor: string | null;
  selectedStudentUserId: string | null;
  focusedStudentRowId: string | null;
  studentSearch: string;
  studentSort: 'name-asc' | 'name-desc';
  rosterScrollTop: { desktop: number; mobile: number };
  toggleStudent(name: string): void;
  clearStudents(): void;
  setGuardianChannel(channel: GuardianChannel): void;
  setPresentingFor(student: string | null): void;
  setStudentSelection(userId: string | null, rowId?: string | null): void;
  setStudentSearch(search: string): void;
  setStudentSort(sort: 'name-asc' | 'name-desc'): void;
  captureRosterPosition(
    surface: 'desktop' | 'mobile',
    scrollTop: number,
    rowId: string | null,
  ): void;
  clearStudentSelection(): void;
  reset(): void;
}

export const useClassroomUiStore = create<ClassroomUiState>((set) => ({
  selectedStudents: [],
  guardianChannel: 'line',
  presentingFor: null,
  selectedStudentUserId: null,
  focusedStudentRowId: null,
  studentSearch: '',
  studentSort: 'name-asc',
  rosterScrollTop: { desktop: 0, mobile: 0 },

  toggleStudent: (name) =>
    set((state) => ({
      selectedStudents: state.selectedStudents.includes(name)
        ? state.selectedStudents.filter((item) => item !== name)
        : [...state.selectedStudents, name],
    })),
  clearStudents: () => set({ selectedStudents: [] }),
  setGuardianChannel: (guardianChannel) => set({ guardianChannel }),
  setPresentingFor: (presentingFor) => set({ presentingFor }),
  setStudentSelection: (selectedStudentUserId, focusedStudentRowId = null) =>
    set({ selectedStudentUserId, focusedStudentRowId }),
  setStudentSearch: (studentSearch) => set({ studentSearch }),
  setStudentSort: (studentSort) => set({ studentSort }),
  captureRosterPosition: (surface, scrollTop, focusedStudentRowId) =>
    set((state) => ({
      focusedStudentRowId,
      rosterScrollTop: { ...state.rosterScrollTop, [surface]: scrollTop },
    })),
  clearStudentSelection: () => set({ selectedStudentUserId: null, focusedStudentRowId: null }),
  reset: () =>
    set({
      selectedStudents: [],
      guardianChannel: 'line',
      presentingFor: null,
      selectedStudentUserId: null,
      focusedStudentRowId: null,
      studentSearch: '',
      studentSort: 'name-asc',
      rosterScrollTop: { desktop: 0, mobile: 0 },
    }),
}));

registerWorkspaceTransientReset('classrooms-ui', () => useClassroomUiStore.getState().reset());
